/**
 * @author: lv.chen
 * @date: 2026/01/06
 * @desc: Quarter selector component for quarterly report
 */
import React from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { Dropdown, List } from 'tea-component';
import { getNullableMoment } from '@intelligence/v3/utils/date';

import './index.scss';

interface IQuarterSelectorProps extends IBaseProps {
  currentDate: string;
  options: IQuarterOption[];
  onQuarterChange: (date: string) => void;
}

const formatQuarterDate = (date?: string): string => {
  const dateM = getNullableMoment(date);
  if (dateM === null) return '';
  return `${dateM.format('YYYY')} Q${dateM.format('Q')}`;
};

const QuarterSelector: React.FC<IQuarterSelectorProps> = (props) => {
  const { currentDate, options, onQuarterChange, className, style } = props;
  const { formatMessage } = useIntl();

  const currentOption = options.find((opt) => opt.date === currentDate);

  const handleSelect = (option: IQuarterOption) => {
    if (option.available) {
      onQuarterChange(option.date);
    }
  };

  return (
    <Dropdown
      className={classnames('dbu-quarter-selector', className)}
      style={style}
      appearance="button"
      button={
        <div className="dbu-quarter-selector__trigger">
          <div className="dbu-quarter-selector__icon"></div>
          <div>{formatMessage({ id: 'dbu_common_quarter' })}</div>
          <div className="dbu-quarter-selector__splitter"></div>
          <div>{currentOption?.label || formatQuarterDate(currentDate)}</div>
        </div>
      }
    >
      <List type="option" className="dbu-quarter-selector__list">
        {options.map((option) => (
          <List.Item
            key={option.date}
            className={classnames('dbu-quarter-selector__option', {
              'dbu-quarter-selector__option--disabled': !option.available,
            })}
            selected={option.date === currentDate}
            onClick={() => handleSelect(option)}
          >
            <span>{option.label}</span>
            {!option.available && (
              <span className="dbu-quarter-selector__unavailable-hint">
                {formatMessage({ id: 'dbu_quarter_report_unavailable' })}
              </span>
            )}
          </List.Item>
        ))}
      </List>
    </Dropdown>
  );
};

export default QuarterSelector;
